import { Controller } from "@hotwired/stimulus"
import { changedFilterParams, filterBarSelections, frameAffected, toParams } from "../lib/analytics_filters"

export default class extends Controller {
  static targets = ["startDate", "endDate", "preset", "presetLabel"]
  static values = { activePreset: String, questionId: Number }

  // What the result frames were rendered with — a later change reloads only the frames it affects.
  connect() {
    this.appliedSelections = filterBarSelections(this.element)
  }

  // A preset just adjusts the always-visible date range: resolve its dates, fill the picker, apply.
  selectPreset(event) {
    const btn = event.currentTarget
    const preset = btn.dataset.preset
    this.activePresetValue = preset
    this.highlightPreset(btn)
    this.fetchPresetDates(preset).then(({ start_date, end_date }) => {
      this.startDateTarget.value = start_date
      this.endDateTarget.value = end_date
      this.syncDatePicker()
      this.applyFilters()
    })
  }

  // A manual edit to the date field is a custom range — drop the preset and label the trigger "Custom".
  dateRangeSelected() {
    this.clearPresetHighlight()
    this.activePresetValue = ""
    if (this.hasPresetLabelTarget) this.presetLabelTarget.textContent = "Custom"
    this.applyFilters()
  }

  // Round-trips through the server so the resolved dates use the storefront's timezone instead
  // of the browser's clock — the chart and the drill-down then see the same window.
  async fetchPresetDates(preset) {
    const url = new URL("/analytics/preset_dates", window.location.origin)
    url.searchParams.set("preset", preset)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    return response.json()
  }

  syncDatePicker() {
    const start = this.startDateTarget.value
    const end = this.endDateTarget.value
    if (!start || !end) return

    const control = this.element.querySelector("[data-datetime-picker-target='control']")
    if (!control) return

    const s = new Date(start + "T00:00:00")
    const e = new Date(end + "T00:00:00")

    if (control._flatpickr) {
      control._flatpickr.setDate([s, e], false)
      control.value = `${control._flatpickr.formatDate(s, "m/d/Y")} to ${control._flatpickr.formatDate(e, "m/d/Y")}`
    } else {
      const fmt = (d) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`
      control.value = `${fmt(s)} to ${fmt(e)}`
    }
  }

  highlightPreset(activeBtn) {
    this.presetTargets.forEach(btn => this.setPresetActive(btn, btn === activeBtn))
    if (this.hasPresetLabelTarget) {
      this.presetLabelTarget.textContent = (activeBtn.querySelector("span") || activeBtn).textContent.trim()
    }
  }

  clearPresetHighlight() {
    this.presetTargets.forEach(btn => this.setPresetActive(btn, false))
  }

  setPresetActive(btn, on) {
    btn.querySelector("[data-check]")?.classList.toggle("hidden", !on)
    btn.classList.toggle("text-violet-700", on)
    btn.classList.toggle("font-medium", on)
    btn.classList.toggle("bg-violet-50", on)
    btn.classList.toggle("text-gray-700", !on)
  }

  applyFilters() {
    const selections = filterBarSelections(this.element)
    // Dates are required only when the bar has them — a bar without filterable dates can still change its
    // other filters.
    if (this.hasStartDateTarget && (!selections.start_date || !selections.end_date)) return

    const params = toParams(selections)
    if (this.activePresetValue) params.set("preset", this.activePresetValue)
    // This page's request order: a dashboard tile orders its requests by it, so a late older one never
    // supersedes a newer one (Analytics::DashboardItemRequest).
    this.requestSeq = (this.requestSeq || 0) + 1
    params.set("seq", this.requestSeq)

    const loadingInner = '<div class="flex items-center justify-center h-full"><div class="animate-pulse text-xs text-gray-400">Loading...</div></div>'

    const changed = changedFilterParams(this.appliedSelections, selections)
    this.appliedSelections = selections

    const frames = document.querySelectorAll("turbo-frame[id^='dashboard_item_'], turbo-frame[id^='ai-'][id$='-panel']")
    frames.forEach(frame => {
      const baseSrc = frame.dataset.baseSrc
      if (!baseSrc || !frameAffected(frame, changed)) return

      params.set("_t", Date.now())
      const newFrame = document.createElement("turbo-frame")
      newFrame.id = frame.id
      newFrame.dataset.baseSrc = baseSrc
      if (frame.dataset.filterParams !== undefined) newFrame.dataset.filterParams = frame.dataset.filterParams
      const separator = baseSrc.includes("?") ? "&" : "?"
      newFrame.setAttribute("src", `${baseSrc}${separator}${params}`)
      newFrame.setAttribute("loading", "eager")
      const isDashboardItem = frame.id.startsWith("dashboard_item_")
      newFrame.innerHTML = isDashboardItem ? loadingInner : `<div class="dashboard-card">${loadingInner}</div>`
      newFrame.className = frame.className
      frame.replaceWith(newFrame)
    })

    this.dispatch("applied")
    // The server saves the preset when one is active (its dates stay rolling), else the picked range.
    this.saveQuestionDefaults({ preset: this.activePresetValue, ...selections })
  }

  saveQuestionDefaults(filters) {
    if (!this.hasQuestionIdValue || !this.questionIdValue) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(`/analytics/questions/${this.questionIdValue}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ default_filters: filters })
    })
  }
}
